// Metas multi-área (grupos e submetas) — lógica pura.
// Áreas são taxonomia FIXA: dado estruturado e comparável entre usuários
// (insumo do benchmark F5). Hierarquia de 1 nível: grupo → submetas.
// Progresso unificado: numérico (peso/dinheiro) > horas > conteúdos > média dos filhos.

import type { Content, Goal, GoalArea, Session } from '@app-types/domain';

export interface AreaMeta {
  id: GoalArea;
  label: string;
  icon: string;
  color: string; // var CSS
}

export const GOAL_AREAS: AreaMeta[] = [
  { id: 'carreira', label: 'CARREIRA', icon: '💼', color: 'var(--cyan)' },
  { id: 'educacao', label: 'EDUCAÇÃO', icon: '🎓', color: 'var(--purple)' },
  { id: 'saude', label: 'SAÚDE', icon: '💪', color: 'var(--green)' },
  { id: 'financas', label: 'FINANÇAS', icon: '💰', color: 'var(--gold, #ffd700)' },
  { id: 'pessoal', label: 'PESSOAL', icon: '✨', color: 'var(--pink)' },
];

/** metas antigas não têm area → carreira (default do produto) */
export function areaOf(goal: Goal): GoalArea {
  return goal.area ?? 'carreira';
}

/** metas raiz (sem pai) de uma área, ordenadas por prioridade */
export function rootsOfArea(goals: Goal[], area: GoalArea): Goal[] {
  return goals
    .filter((g) => areaOf(g) === area && !g.parent_id)
    .sort((a, b) => a.priority - b.priority || a.created_at.localeCompare(b.created_at));
}

/** submetas de um grupo, ordenadas por prioridade e data-alvo */
export function childrenOf(goals: Goal[], parentId: string): Goal[] {
  return goals
    .filter((g) => g.parent_id === parentId)
    .sort(
      (a, b) =>
        a.priority - b.priority ||
        (a.target_date ?? '9999').localeCompare(b.target_date ?? '9999') ||
        a.created_at.localeCompare(b.created_at),
    );
}

export interface GoalProgress {
  pct: number | null; // 0-100 · null = sem como medir ainda
  label: string; // texto curto pro card, ex: "84 de 80 kg" · "41h de 120h"
  kind: 'value' | 'hours' | 'contents' | 'group' | 'none';
}

function clampPct(n: number): number {
  return Math.max(0, Math.min(100, Math.round(n)));
}

/** progresso de meta numérica — funciona nos dois sentidos (juntar R$ ou perder kg) */
export function valueProgress(goal: Goal): GoalProgress | null {
  const { start_value: start, target_value: target } = goal;
  if (start === undefined || target === undefined || start === target) return null;
  const current = goal.current_value ?? start;
  const pct = clampPct(((current - start) / (target - start)) * 100);
  const unit = goal.unit ? ` ${goal.unit}` : '';
  return { pct, label: `${current}${unit} → alvo ${target}${unit}`, kind: 'value' };
}

/** horas concluídas vinculadas à meta */
export function hoursDone(goalId: string, sessions: Session[]): number {
  return sessions
    .filter((s) => s.goal_id === goalId && s.status === 'completed')
    .reduce((sum, s) => sum + (s.actual_duration_min || 0) / 60, 0);
}

/**
 * Progresso unificado de uma meta:
 * 1. numérica (start/target_value) — check-ins
 * 2. horas (target_hours) — sessões concluídas
 * 3. conteúdos vinculados — done/total
 * 4. grupo — média dos filhos que têm progresso
 */
export function goalProgress(
  goal: Goal,
  goals: Goal[],
  contents: Content[],
  sessions: Session[],
): GoalProgress {
  const byValue = valueProgress(goal);
  if (byValue) return byValue;

  if (goal.target_hours && goal.target_hours > 0) {
    const h = hoursDone(goal.id, sessions);
    return {
      pct: clampPct((h / goal.target_hours) * 100),
      label: `${h.toFixed(1)}h de ${goal.target_hours}h`,
      kind: 'hours',
    };
  }

  const linked = contents.filter((c) => c.goalId === goal.id);
  if (linked.length > 0) {
    const done = linked.filter((c) => c.status === 'done').length;
    return {
      pct: clampPct((done / linked.length) * 100),
      label: `${done}/${linked.length} conteúdos`,
      kind: 'contents',
    };
  }

  const kids = childrenOf(goals, goal.id);
  if (kids.length > 0) {
    const pcts = kids
      .map((k) => goalProgress(k, goals, contents, sessions).pct)
      .filter((p): p is number => p !== null);
    if (pcts.length > 0) {
      const avg = clampPct(pcts.reduce((a, b) => a + b, 0) / pcts.length);
      const achieved = kids.filter((k) => k.status === 'achieved').length;
      return { pct: avg, label: `${achieved}/${kids.length} submetas atingidas`, kind: 'group' };
    }
    return { pct: null, label: `${kids.length} submetas`, kind: 'group' };
  }

  return { pct: null, label: 'sem medição ainda', kind: 'none' };
}

/** dias até a data-alvo (null sem data; negativo = passou) */
export function daysToTarget(goal: Goal, now = new Date()): number | null {
  if (!goal.target_date) return null;
  return Math.ceil((new Date(goal.target_date).getTime() - now.getTime()) / 86_400_000);
}

/** distribuição de horas concluídas por área (para o resumo do topo) */
export function hoursByArea(goals: Goal[], sessions: Session[]): Record<GoalArea, number> {
  const acc = { carreira: 0, educacao: 0, saude: 0, financas: 0, pessoal: 0 } as Record<
    GoalArea,
    number
  >;
  const areaById = new Map(goals.map((g) => [g.id, areaOf(g)]));
  for (const s of sessions) {
    if (s.status !== 'completed' || !s.goal_id) continue;
    const area = areaById.get(s.goal_id);
    if (area) acc[area] += (s.actual_duration_min || 0) / 60;
  }
  return acc;
}
